import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { Seg } from '../../components/Seg';
import { Ic } from '../../components/Ic';
import { StatusPill } from '../../components/StatusDot';
import { deriveScraperState } from '../../lib/scraperState';
import { ScraperActions } from './ScraperActions';
import { MetricsTab } from './tabs/MetricsTab';
import { LogsTab } from './tabs/LogsTab';
import { CodeTab } from './tabs/CodeTab';
import { HistoryTab } from './tabs/HistoryTab';
import type { Scraper } from '../../api/types';

export const DETAIL_TABS = ['metrics', 'logs', 'code', 'history'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

export function isDetailTab(value: string | undefined): value is DetailTab {
  return !!value && (DETAIL_TABS as readonly string[]).includes(value);
}

const TAB_OPTIONS = [
  { value: 'metrics' as const, label: 'Metrics', icon: Ic.trend },
  { value: 'logs' as const, label: 'Logs', icon: Ic.cmd },
  { value: 'code' as const, label: 'Code', icon: Ic.code },
  { value: 'history' as const, label: 'History', icon: Ic.history },
];

export function ScraperDetailModal({
  scraper,
  tab,
  open,
  onExited,
}: {
  scraper: Scraper;
  tab: DetailTab;
  open?: boolean;
  onExited?: () => void;
}) {
  const navigate = useNavigate();
  const state = deriveScraperState(scraper);
  const encoded = encodeURIComponent(scraper.name);

  return (
    <Modal
      size="lg"
      title={scraper.name}
      icon={Ic.box}
      onClose={() => navigate('/scrapers')}
      open={open}
      onExited={onExited}
      footer={
        <>
          <ScraperActions scraper={scraper} />
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={() => navigate('/scrapers')}>
            Close
          </button>
        </>
      }
    >
      <div className="detail-head">
        <StatusPill state={state} />
        <span className="mono-sm muted">{scraper.file || 'template scraper'}</span>
        <span className="grow" />
        {/* Tab lives in the URL, so /app/scrapers/nikeau/logs is linkable. */}
        <Seg
          value={tab}
          onChange={(next) => navigate(`/scrapers/${encoded}/${next}`)}
          options={TAB_OPTIONS}
          ariaLabel="Detail section"
        />
      </div>

      {tab === 'metrics' && <MetricsTab scraper={scraper} />}
      {tab === 'logs' && <LogsTab name={scraper.name} />}
      {tab === 'code' && <CodeTab name={scraper.name} />}
      {tab === 'history' && <HistoryTab name={scraper.name} />}
    </Modal>
  );
}
